import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles, faCopy, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import {
  MARKETING_PRESETS, PRESET_CATEGORIES, PRESET_DEFAULTS, PLATFORMS,
  buildPresetPrompt, presetsByCategory, findPlatform, resolveAspect,
} from "./marketingPresets";

// Marketing Studio — galerie de formats de pub prêts à l'emploi. On décrit son
// produit une seule fois, puis chaque carte compose le prompt complet et le
// charge dans le composeur du Studio (aucun crédit débité tant qu'on ne génère pas).
const BRIEF_FIELDS = [
  { key: "product", label: "Produit", placeholder: "Ex : une crème hydratante bio en pot de verre" },
  { key: "avatar", label: "Avatar", placeholder: PRESET_DEFAULTS.avatar },
  { key: "setting", label: "Décor", placeholder: PRESET_DEFAULTS.setting },
  { key: "hook", label: "Hook (texte à l'écran)", placeholder: "Ex : J'ai arrêté d'acheter des crèmes à 60 €" },
  { key: "cta", label: "Appel à l'action", placeholder: "Ex : Commandez avant dimanche" },
];

const MarketingPresetGallery = ({ onApply, collapsedByDefault = false }) => {
  const [open, setOpen] = useState(!collapsedByDefault);
  const [category, setCategory] = useState("Tous");
  const [brief, setBrief] = useState({ product: "", avatar: "", setting: "", hook: "", cta: "" });
  const [previewKey, setPreviewKey] = useState(null);
  // Régie visée : ajuste le ratio, le montage et la fin de plan du prompt.
  const [platformKey, setPlatformKey] = useState(null);

  const platform = useMemo(() => findPlatform(platformKey), [platformKey]);
  const visible = useMemo(() => presetsByCategory(category), [category]);
  const prompts = useMemo(
    () => Object.fromEntries(MARKETING_PRESETS.map((p) => [p.key, buildPresetPrompt(p, brief, platform)])),
    [brief, platform]
  );

  const apply = (preset) => onApply?.({
    presetKey: preset.key,
    prompt: prompts[preset.key],
    aspect: resolveAspect(preset, platform),
    motion: preset.motion,
    capability: preset.capability,
    label: platform ? `${preset.label} · ${platform.label}` : preset.label,
  });

  const copy = (preset) => navigator.clipboard?.writeText(prompts[preset.key]);

  return (
    <div className="rounded-2xl bg-gradient-to-b from-[#1a1640] via-[#221C51] to-[#2e265e] p-5 shadow-lg mt-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-fuchsia-300/80">Marketing Studio</p>
          <p className="text-lg font-bold text-white">Transformez n'importe quel produit en pub vidéo</p>
          <p className="text-xs text-indigo-200/70 mt-1 max-w-xl">
            Décrivez votre produit, choisissez un format : le prompt complet se charge dans le composeur ci-dessous.
          </p>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-xs font-semibold text-indigo-100/80 hover:text-white bg-white/10 px-3 py-1.5 rounded-full"
        >
          {open ? "Réduire" : `Voir les ${MARKETING_PRESETS.length} formats`}
        </button>
      </div>

      {open && (
        <>
          {/* Brief produit — alimente les {product} / {avatar} / {setting} de chaque préréglage */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {BRIEF_FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="block text-[11px] font-semibold text-indigo-200/70 mb-1">{f.label}</span>
                <input
                  value={brief[f.key]}
                  onChange={(e) => setBrief((b) => ({ ...b, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 text-sm rounded-xl bg-white/10 border border-white/15 text-white placeholder-indigo-200/40 focus:outline-none focus:border-fuchsia-400"
                />
              </label>
            ))}
          </div>

          {/* Régie visée — adapte ratio, montage et fin de plan */}
          <div className="mt-4">
            <p className="text-[11px] font-semibold text-indigo-200/70 mb-2">Régie visée</p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setPlatformKey(null)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                  !platformKey
                    ? "bg-white text-[#221C51] border-white"
                    : "border-white/15 text-indigo-100/70 hover:border-white/30 hover:text-white"
                }`}
              >
                Neutre
              </button>
              {PLATFORMS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPlatformKey((k) => (k === p.key ? null : p.key))}
                  title={p.blurb}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                    platformKey === p.key
                      ? "bg-white text-[#221C51] border-white"
                      : "border-white/15 text-indigo-100/70 hover:border-white/30 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {platform && <p className="text-[11px] text-indigo-200/50 mt-1.5">{platform.blurb}</p>}
          </div>

          {/* Filtres de catégorie */}
          <div className="flex gap-2 flex-wrap mt-4">
            {PRESET_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                  category === cat
                    ? "bg-fuchsia-600 text-white border-fuchsia-600"
                    : "border-white/15 text-indigo-100/70 hover:border-white/30 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grille de formats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {visible.map((p) => (
              <div key={p.key} className="rounded-xl bg-white/5 border border-white/10 p-3 hover:border-fuchsia-400/50 transition flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">{p.label}</p>
                    <p className="text-[11px] text-indigo-200/60 mt-0.5">{p.blurb}</p>
                  </div>
                  <span className="shrink-0 text-[10px] font-semibold text-fuchsia-200 bg-fuchsia-500/20 px-2 py-0.5 rounded-full">{resolveAspect(p, platform)}</span>
                </div>

                <div className="flex gap-1.5 flex-wrap mt-2">
                  {p.tags.map((t) => (
                    <span key={t} className="text-[10px] text-indigo-200/60 bg-white/5 px-1.5 py-0.5 rounded">{t}</span>
                  ))}
                </div>

                {previewKey === p.key && (
                  <p className="text-[11px] text-indigo-100/70 mt-2 leading-relaxed max-h-32 overflow-y-auto">
                    {prompts[p.key]}
                  </p>
                )}

                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/10">
                  <button
                    onClick={() => apply(p)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white hover:opacity-90"
                  >
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[11px]" />
                    Utiliser
                  </button>
                  <button
                    onClick={() => setPreviewKey((k) => (k === p.key ? null : p.key))}
                    title="Aperçu du prompt"
                    className="px-2 py-1.5 rounded-lg text-xs text-indigo-100/70 hover:text-white hover:bg-white/10"
                  >
                    <FontAwesomeIcon icon={previewKey === p.key ? faEyeSlash : faEye} />
                  </button>
                  <button
                    onClick={() => copy(p)}
                    title="Copier le prompt"
                    className="px-2 py-1.5 rounded-lg text-xs text-indigo-100/70 hover:text-white hover:bg-white/10"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingPresetGallery;
